import { genericMemo } from "@redotech/react-util/component";
import { ArrayPredicateExpression } from "@redotech/redo-model/advanced-flow/expressions/array-expression";
import { BooleanEvaluationExpression } from "@redotech/redo-model/advanced-flow/expressions/boolean-expression";
import { CheckoutContextPredicateExpression } from "@redotech/redo-model/advanced-flow/expressions/checkout-context-predicate";
import { SchemaBooleanExpression } from "@redotech/redo-model/advanced-flow/expressions/index";
import { NumberComparisonExpression } from "@redotech/redo-model/advanced-flow/expressions/number-expression";
import { TextMatchExpression } from "@redotech/redo-model/advanced-flow/expressions/text-expression";
import { GenericDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/generic-types";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { DeepPartial } from "@redotech/util/type";
import { ArrayInspector } from "./array-inspector";
import { BooleanInspector } from "./boolean-inspector";
import { CheckoutContextPredicateInspector } from "./checkout-context-predicate-inspector";
import { EnumInspector } from "./enum-inspector";
import { NumberInspector } from "./number-inspector";
import { TextInspector } from "./text-inspector";
import { TokenInspector } from "./token-inspector";

export const FieldInspector = genericMemo(function FieldInspector({
  schema,
  dataType,
  selectedField,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  dataType: GenericDataType;
  selectedField: Schema["fields"][string];
  expression: DeepPartial<SchemaBooleanExpression>;
  setExpression: (expression: DeepPartial<SchemaBooleanExpression>) => void;
  showValidationErrors?: boolean;
}) {
  if (dataType.startsWith("Multiple")) {
    return (
      <ArrayInspector
        dataType={dataType}
        expression={expression as DeepPartial<ArrayPredicateExpression>}
        schema={schema}
        selectedField={selectedField}
        setExpression={setExpression}
        showValidationErrors={showValidationErrors}
      />
    );
  }
  if (dataType === "Text" || dataType === "Url" || dataType === "Maybe Text") {
    const suggestionType =
      "suggestionType" in selectedField
        ? selectedField.suggestionType
        : undefined;
    return (
      <TextInspector
        dataType={dataType}
        expression={expression as DeepPartial<TextMatchExpression>}
        schema={schema}
        setExpression={setExpression}
        showValidationErrors={showValidationErrors}
        suggestionType={suggestionType}
      />
    );
  }
  if (dataType === "Boolean") {
    return (
      <BooleanInspector
        dataType={dataType}
        expression={expression as DeepPartial<BooleanEvaluationExpression>}
        schema={schema}
        setExpression={setExpression}
      />
    );
  }
  if (dataType === "Enum") {
    return (
      <EnumInspector
        dataType={dataType}
        expression={expression as DeepPartial<TextMatchExpression>}
        schema={schema}
        setExpression={setExpression}
        showValidationErrors={showValidationErrors}
      />
    );
  }
  if (dataType === "Token") {
    return (
      <TokenInspector
        dataType={dataType}
        expression={expression as DeepPartial<TextMatchExpression>}
        schema={schema}
        setExpression={setExpression}
      />
    );
  }
  if (dataType === "Number" || dataType === "Maybe Number") {
    return (
      <NumberInspector
        dataType={dataType}
        expression={expression as DeepPartial<NumberComparisonExpression>}
        schema={schema}
        setExpression={setExpression}
        showValidationErrors={showValidationErrors}
      />
    );
  }
  if (dataType === "Checkout Context Predicate") {
    return (
      <CheckoutContextPredicateInspector
        dataType={dataType}
        expression={
          expression as DeepPartial<CheckoutContextPredicateExpression>
        }
        schema={schema}
        setExpression={setExpression}
        showValidationErrors={showValidationErrors}
      />
    );
  }
  return <>Unsupported step type {(console.error(dataType), dataType)}</>;
});
